/**
 * Implementation of the PreprocessorManager interface.
 * Manages a collection of preprocessor plugins and executes them in sequence.
 *
 * @class MessagePreprocessorManager
 * @implements {PreprocessorManager}
 *
 * @example
 * ```typescript
 * const manager = new MessagePreprocessorManager();
 * manager.addPlugin(new FinnishSSNAnonymizer());
 * const processedMessage = await manager.processMessage(message);
 * ```
 */
export class MessagePreprocessorManager {
    constructor() {
        /**
         * Internal storage for registered plugins.
         * @private
         */
        this._plugins = [];
    }
    /**
     * Get the list of registered plugins.
     * Returns a copy to prevent external modification.
     *
     * @returns {PreprocessorPlugin[]} Array of registered plugins
     */
    get plugins() {
        return [...this._plugins];
    }
    /**
     * Register a new preprocessor plugin.
     * Plugins are executed in the order they are added.
     *
     * @param {PreprocessorPlugin} plugin - The plugin to add
     */
    addPlugin(plugin) {
        if (this._plugins.some((p) => p.name === plugin.name)) {
            throw new Error(`Plugin ${plugin.name} is already registered`);
        }
        this._plugins.push(plugin);
    }
    /**
     * Process a message through all registered plugins.
     * Each plugin receives the output of the previous plugin.
     * If a plugin throws an error, it's logged and the chain continues.
     *
     * @param {ChatMessage} message - The message to process
     * @returns {Promise<ChatMessage>} The final processed message
     */
    async processMessage(content) {
        let processedMessage = content;
        for (const plugin of this._plugins) {
            try {
                processedMessage = await plugin.process(processedMessage);
            }
            catch (error) {
                /* v8 ignore line */
                console.error(`Error in preprocessor plugin ${plugin.name}:`, error);
            }
        }
        return processedMessage;
    }
}
//# sourceMappingURL=PreprocessorManager.js.map